import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Check if running inside a native Capacitor shell.
 * The native bridge injects window.__IS_NATIVE_APP__ = true
 */
export const isNativePlatform = () => {
  return !!(window.__IS_NATIVE_APP__ || (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()));
};

/**
 * Cancel the native call notification.
 */
export const cancelCallNotification = () => {
  // Native notifications are managed by Android NotificationManager
  // We can't cancel from JS when using remote URL, but the timeout (30s) handles it
};

/**
 * Register the native FCM token with the backend.
 * Called after user logs in on the native app.
 * The token is injected by MainActivity.java into window.__NATIVE_FCM_TOKEN__
 */
export const registerNativeFcmToken = async () => {
  if (!isNativePlatform()) return;
  
  const fcmToken = window.__NATIVE_FCM_TOKEN__;
  const authToken = localStorage.getItem('token');
  
  if (!fcmToken || !authToken) {
    console.log('[NativePush] Waiting for tokens... fcm:', !!fcmToken, 'auth:', !!authToken);
    return;
  }

  try {
    await axios.post(`${API}/push/fcm-register`, {
      fcm_token: fcmToken,
      token_type: 'native',
    }, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    console.log('[NativePush] FCM token registered with backend');
  } catch (err) {
    console.error('[NativePush] FCM register failed:', err);
  }
};

/**
 * Initialize native push — poll for the FCM token that gets injected by native code.
 */
export const initNativePush = async () => {
  if (!isNativePlatform()) {
    console.log('[NativePush] Not native platform');
    return false;
  }
  
  // The native code injects the token, but there might be a race condition.
  // Retry a few times with a delay.
  for (let i = 0; i < 5; i++) {
    if (window.__NATIVE_FCM_TOKEN__) {
      await registerNativeFcmToken();
      return true;
    }
    await new Promise(r => setTimeout(r, 1000));
  }
  
  console.warn('[NativePush] FCM token not injected after 5s');
  return false;
};

/**
 * Set up native push listeners — minimal since we handle most in Java.
 */
export const setupNativePushListeners = (onCallReceived, onMessageReceived) => {
  if (!isNativePlatform()) return () => {};
  
  // Re-register FCM token periodically (in case token refreshes)
  const interval = setInterval(() => {
    registerNativeFcmToken();
  }, 60000); // Every 60 seconds

  return () => clearInterval(interval);
};
